import { useEffect, useRef, useState } from 'react';
import { Button, Box, Tooltip } from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../../../api';
import { getSession } from '../../../auth';
import useNotification from '../../Notification';
import { copyToClipboard } from '../../Crm/ContactProfile';

const POLLING_INTERVAL = 5000; // 5 seconds
const MAX_POLLING_ATTEMPTS = 60; // 2 minutes maximum

export const TranscriptionButton = ({
    message_id,
    message_transcription,
    message_transcription_status,
    message_type,
    transcriptionEndpointURL,
    updateMessage
}) => {
    const [isPolling, setIsPolling] = useState(false);

    const pollingTimeoutRef = useRef(null);
    const pollingAttemptsRef = useRef(0);
    const isMountedRef = useRef(true);

    const user = getSession();
    const audioTranscription =
        user.profile.enterprise?.audio_transcription;

    const hasTranscription =
        message_transcription &&
        message_transcription.length > 0;

    const isInProgress =
        message_transcription_status === "IN_PROGRESS";

    const isDisabled =
        audioTranscription !== true ||
        isInProgress ||
        isPolling;

    const resetTranscription = () => {
        setIsPolling(false);

        updateMessage(
            message_id,
            null,
            null,
            null
        );
    };

    /**
     * Cleanup when component is unmounted.
     */
    useEffect(() => {
        return () => {
            isMountedRef.current = false;

            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
        };
    }, []);

    /**
     * Polls the same transcription endpoint until the
     * transcription is completed.
     */
    const pollTranscription = async () => {
        if (!isMountedRef.current) {
            return;
        }

        if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
            resetTranscription();

            useNotification(
                "Transcrição",
                "A transcrição está demorando mais que o esperado. Tente novamente mais tarde.",
                "warning"
            );

            return;
        }

        pollingAttemptsRef.current += 1;

        try {
            const res = await api.post(transcriptionEndpointURL);

            if (!isMountedRef.current) {
                return;
            }

            /**
             * Transcription completed.
             */
            if (
                res.status === 200 &&
                res.data.status === "COMPLETED"
            ) {
                setIsPolling(false);

                updateMessage(
                    message_id,
                    "COMPLETED",
                    null,
                    res.data.transcription
                );

                return;
            }

            /**
             * Still processing.
             *
             * Schedule another request.
             */
            if (
                res.status === 200 &&
                res.data.status !== "COMPLETED"
            ) {
                pollingTimeoutRef.current = setTimeout(
                    pollTranscription,
                    POLLING_INTERVAL
                );

                return;
            }

            /**
             * Unexpected response.
             */
            resetTranscription();

            useNotification(
                "Ops",
                `Algo deu errado, tente novamente`,
                "danger"
            );
        } catch (error) {
            if (!isMountedRef.current) {
                return;
            }

            resetTranscription();

            useNotification(
                "Transcrição",
                "Não foi possível consultar a transcrição.",
                "danger"
            );
        }
    };

    /**
     * Starts the transcription.
     */
    const handleTranscription = async () => {
        if (
            isDisabled ||
            hasTranscription
        ) {
            return;
        }

        try {
            const res = await api.post(transcriptionEndpointURL);

            /**
             * 201 means the transcription was started.
             */
            if (res.status === 201) {
                updateMessage(
                    message_id,
                    "IN_PROGRESS",
                    res.data.key,
                    null
                );

                setIsPolling(true);
                pollingAttemptsRef.current = 0;

                useNotification(
                    "Transcrição",
                    "Transcrição iniciada, por favor aguarde.",
                    "success"
                );

                /**
                 * Start polling after 2 seconds.
                 */
                pollingTimeoutRef.current = setTimeout(
                    pollTranscription,
                    POLLING_INTERVAL
                );

                return;
            }

            /**
             * The endpoint may already return the result
             * immediately in some cases.
             */
            if (res.status === 200) {
                if (res.data.status === "COMPLETED") {
                    updateMessage(
                        message_id,
                        "COMPLETED",
                        null,
                        res.data.transcription
                    );

                    return;
                }

                /**
                 * Already in progress.
                 * Start polling.
                 */
                setIsPolling(true);
                pollingAttemptsRef.current = 0;

                pollingTimeoutRef.current = setTimeout(
                    pollTranscription,
                    POLLING_INTERVAL
                );

                return;
            }

            if (res.status >= 400) {
                useNotification(
                    "Transcrição",
                    `Erro! ${res.data.error || "Erro desconhecido"}`,
                    "danger"
                );
            }
        } catch (error) {
            useNotification(
                "Transcrição",
                "Não foi possível iniciar a transcrição.",
                "danger"
            );
        }
    };

    /**
     * Don't render anything for non-audio messages.
     *
     * This comes AFTER the hooks so that the hook order
     * remains consistent between renders.
     */
    if (message_type !== "audioMessage") {
        return null;
    }

    /**
     * Once transcription is available, show it.
     */
    if (hasTranscription) {
        return (
            <Box
                onClick={() => copyToClipboard(message_transcription)}
                sx={{
                    mt: 0.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    backgroundColor:
                        "rgba(81, 102, 166, 0.08)",
                    border:
                        "1px solid rgba(81, 102, 166, 0.18)",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    lineHeight: 1.4,
                    color: "text.primary",
                    transition: "all 0.2s ease",

                    "&:hover": {
                        backgroundColor:
                            "rgba(81, 102, 166, 0.14)",
                        borderColor:
                            "rgba(81, 102, 166, 0.35)",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                    }}
                >
                    <RecordVoiceOverIcon
                        sx={{
                            fontSize: "1.1rem",
                            color: "#5166a6",
                            mt: "2px",
                            flexShrink: 0,
                        }}
                    />

                    <Box sx={{ flex: 1 }}>
                        {message_transcription}
                    </Box>

                    <ContentCopyIcon
                        sx={{
                            fontSize: "0.95rem",
                            color: "text.secondary",
                            mt: "2px",
                            flexShrink: 0,
                        }}
                    />
                </Box>
            </Box>
        );
    }

    const tooltipTitle =
        audioTranscription !== true
            ? "Módulo de transcrição indisponível para sua conta."
            : "";

    return (
        <Tooltip
            title={tooltipTitle}
            placement="top"
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    disabled={isDisabled}
                    onClick={handleTranscription}
                    startIcon={
                        <RecordVoiceOverIcon
                            sx={{
                                fontSize:
                                    "1.1rem !important",
                            }}
                        />
                    }
                    sx={{
                        mt: 0.5,
                        minHeight: 32,
                        px: 1.5,
                        borderRadius: 1.5,
                        textTransform: "none",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: "#5166a6",
                        borderColor:
                            "rgba(81, 102, 166, 0.45)",
                        backgroundColor:
                            "rgba(81, 102, 166, 0.04)",
                        transition: "all 0.2s ease",

                        "&:hover": {
                            backgroundColor: "#5166a6",
                            borderColor: "#5166a6",
                            color: "#fff",
                        },

                        "&.Mui-disabled": {
                            opacity: 0.6,
                        },
                    }}
                >
                    {isInProgress || isPolling
                        ? "Transcrevendo..."
                        : "Transcrever"}
                </Button>
            </Box>
        </Tooltip>
    );
};
